<template>
  <div class="kds-picker">
    <!-- top bar -->
    <div class="kds-picker__bar">
      <div class="d-flex align-items-center gap-2 text-white-50 small">
        <i class="fa-solid fa-kitchen-set text-warning" />
        <span>{{ $t('KDS Mode') }}</span>
      </div>
      <button class="kds-picker__back" @click="goBack">
        <i class="fa-solid fa-arrow-left me-1" />
        {{ $t('Change Branch') }}
      </button>
    </div>

    <div class="kds-picker__body">
      <!-- hero -->
      <div class="text-center mb-5">
        <div class="kds-picker__logo">
          <i class="fa-solid fa-kitchen-set" />
        </div>
        <h3 class="text-white fw-bold mb-1">{{ $t('Select Station') }}</h3>
        <p class="text-white-50 mb-3">{{ $t('Choose the screen you want to manage') }}</p>
        <span v-if="branchName" class="kds-picker__branch">
          <i class="fa-solid fa-store me-2" />{{ branchName }}
        </span>
      </div>

      <!-- loading -->
      <div v-if="loading" class="text-center text-white-50 py-5">
        <i class="fa-solid fa-spinner fa-spin fa-2x" />
      </div>

      <!-- denied -->
      <div v-else-if="!hasPermission" class="mx-auto" style="max-width: 520px;">
        <access-denied />
      </div>

      <!-- error -->
      <div v-else-if="error" class="kds-picker__error">
        <i class="fa-solid fa-circle-exclamation kds-picker__error-icon" />
        <div class="text-white fw-semibold mb-1">{{ $t('Failed to load stations') }}</div>
        <div class="text-white-50 small mb-4">{{ error }}</div>
        <div class="d-flex gap-2 justify-content-center">
          <button class="btn btn-warning btn-sm fw-semibold" @click="fetchStations">
            <i class="fa-solid fa-rotate-right me-1" />{{ $t('Try again') }}
          </button>
          <button class="kds-picker__back" @click="goBack">
            <i class="fa-solid fa-arrow-left me-1" />{{ $t('Change Branch') }}
          </button>
        </div>
      </div>

      <!-- stations -->
      <div v-else class="kds-picker__grid">
        <button
          v-for="station in stations"
          :key="station.id ?? 'all'"
          class="kds-station"
          :class="{ 'kds-station--all': station.is_all }"
          :style="!station.is_all ? { '--accent': station.color || '#2563eb' } : {}"
          @click="enter(station)"
        >
          <span class="kds-station__accent" />
          <span class="kds-station__icon">
            <i :class="station.is_all ? 'fa-solid fa-layer-group' : 'fa-solid fa-fire-burner'" />
          </span>
          <span class="kds-station__name">{{ $t(station.name) }}</span>

          <span class="kds-station__cats">
            <template v-if="station.is_all">
              <span class="kds-station__all-label">{{ $t('All categories') }}</span>
            </template>
            <template v-else>
              <span
                v-for="cat in station.categories"
                :key="cat.id"
                class="kds-station__chip"
              >{{ $ln(cat) }}</span>
            </template>
          </span>

          <span
            class="kds-station__count"
            :class="{ 'is-hot': station.pending_count > 0 }"
          >
            <i class="fa-solid fa-fire me-1" />
            {{ station.pending_count }} {{ $t('Orders') }}
          </span>
        </button>

        <div v-if="!stations.length" class="text-center text-white-50 py-5 w-100">
          <i class="fa-solid fa-inbox fa-2x mb-2 opacity-50" /><br>
          {{ $t('No Available Data') }}
        </div>
      </div>
    </div>

    <!-- entering overlay -->
    <div v-if="entering" class="kds-picker__overlay">
      <i class="fa-solid fa-spinner fa-spin fa-2x text-warning" />
    </div>
  </div>
</template>

<script lang="js">
import AccessDenied from "@/plugins/AccessDenied.vue";
import { KDSRegisters } from '@/services/endpoint/kds.service';
import { useKDSStore } from '@/stores/kds.store';

export default {
  name: 'KDSStationSelect',
  components: { AccessDenied },
  data() {
    return {
      branchId: Number(this.$route.params.branchId),
      branchName: '',
      stations: [],
      loading: true,
      hasPermission: true,
      error: null,
      entering: false
    };
  },
  async mounted() {
    await this.fetchStations();
  },
  methods: {
    async fetchStations() {
      this.loading = true;
      this.error = null;
      try {
        const res = await KDSRegisters.stations(this.branchId);
        const d = res.data.data || {};
        this.branchName = d.branch || '';
        this.hasPermission = true;

        const realStations = d.stations || [];

        // การ์ด "ทุกหมวด (All)" — FE สร้างเอง (ใช้ยอดรวมคิวจาก all_pending_count)
        const allCard = {
          id: null,
          is_all: true,
          name: 'All',
          color: null,
          pending_count: d.all_pending_count || 0,
          categories: [],
          category_ids: []
        };
        this.stations = [allCard, ...realStations];

        // ไม่มีสถานี (active) ในสาขานี้ → ข้ามหน้าเลือก เข้า KDS Display แบบ "ทุกหมวด" เลย
        // (ตั้ง stations ไว้ก่อนแล้ว ถ้า auto-enter พลาดจะยังมีการ์ด All ให้กดเอง)
        if (realStations.length === 0) {
          await this.enter({ id: null });
        }
      } catch (err) {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
        } else {
          // เช่น branch ไม่ถูกต้อง/ถูกลบ (VALIDATION) หรือ network — โชว์ error state มีปุ่มลองใหม่/กลับ
          this.error = this.$t(err?.response?.data?.message) || this.$t('Failed to load stations');
        }
      } finally {
        this.loading = false;
      }
    },
    async enter(station) {
      this.entering = true;
      try {
        await useKDSStore().enterKds(this.branchId, station.id ?? null);
        this.$router.push({ name: 'kds.display' });
      } catch (err) {
        this.entering = false;
        this.$toast.warn(this.$t(err?.response?.data?.message) || this.$t('Failed to enter KDS'));
      }
    },
    goBack() {
      this.$router.push({ name: 'kds-register' });
    }
  }
};
</script>

<style scoped>
.kds-picker {
  min-height: 100vh;
  background: radial-gradient(1200px 600px at 50% -10%, #1f2a3d 0%, #0f1623 55%, #0b0f17 100%);
  display: flex;
  flex-direction: column;
}

.kds-picker__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}
.kds-picker__back {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #e5e7eb;
  border-radius: 999px;
  padding: 6px 16px;
  font-size: 0.85rem;
  transition: 0.15s;
}
.kds-picker__back:hover {
  background: rgba(255, 255, 255, 0.12);
}

.kds-picker__body {
  flex-grow: 1;
  width: 100%;
  max-width: 1080px;
  margin: 0 auto;
  padding: 48px 24px 64px;
}

.kds-picker__logo {
  width: 76px;
  height: 76px;
  margin: 0 auto 18px;
  border-radius: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #fff;
  background: linear-gradient(135deg, #f59e0b, #ea580c);
  box-shadow: 0 12px 30px rgba(234, 88, 12, 0.35);
}
.kds-picker__branch {
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #fff;
  border-radius: 999px;
  padding: 7px 18px;
  font-weight: 600;
}

.kds-picker__grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
}
.kds-station {
  flex: 0 0 240px;
  max-width: 240px;
}

.kds-station {
  --accent: #2563eb;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: #1a2231;
  border: 1px solid #2a3446;
  border-radius: 18px;
  padding: 26px 18px 22px;
  color: #fff;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}
.kds-station:hover {
  transform: translateY(-5px);
  border-color: var(--accent);
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.45), 0 0 0 1px var(--accent);
}
.kds-station__accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: var(--accent);
}
.kds-station--all {
  border-style: dashed;
  border-color: #46506385;
}
.kds-station--all .kds-station__accent {
  background: linear-gradient(90deg, #64748b, #94a3b8);
}
.kds-station--all .kds-station__icon {
  background: rgba(148, 163, 184, 0.15);
  color: #cbd5e1;
}

.kds-station__icon {
  width: 56px;
  height: 56px;
  margin: 6px 0 14px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  flex-shrink: 0;
  color: var(--accent);
  background: color-mix(in srgb, var(--accent) 16%, transparent);
}
.kds-station__name {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 2.8em;            /* รองรับชื่อ 1-2 บรรทัดให้สูงเท่ากันทุกใบ */
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.25;
  margin-bottom: 12px;
}
.kds-station__cats {
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  justify-content: center;
  gap: 6px;
  flex-grow: 1;                 /* ดัน badge คิวไปชิดล่างให้ตรงกันทุกใบ */
  margin-bottom: 16px;
}
.kds-station__chip {
  font-size: 0.72rem;
  padding: 3px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: #cbd5e1;
}
.kds-station__all-label {
  font-size: 0.8rem;
  color: #94a3b8;
}
.kds-station__count {
  display: inline-flex;
  align-items: center;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 5px 14px;
  border-radius: 999px;
  color: #94a3b8;
  background: rgba(255, 255, 255, 0.05);
}
.kds-station__count.is-hot {
  color: #fff;
  background: #dc2626;
  box-shadow: 0 6px 16px rgba(220, 38, 38, 0.4);
}

.kds-picker__error {
  max-width: 460px;
  margin: 0 auto;
  text-align: center;
  background: #1a2231;
  border: 1px solid #2a3446;
  border-radius: 18px;
  padding: 40px 28px;
}
.kds-picker__error-icon {
  font-size: 2.4rem;
  color: #f59e0b;
  margin-bottom: 14px;
}

.kds-picker__overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1080;
}
</style>
